"use client"

import { tryCreateCryptographicBrowserId } from "./event-id"
import { readMetaClientParams } from "./meta-client-param-builder"

const META_EXTERNAL_ID_STORAGE_KEY = "meta_external_id_v1"
const META_EXTERNAL_ID_COOKIE_NAME = "_meta_external_id"
const AUTH_TOKEN_COOKIE_NAME = "_auth_token"
const META_MARKETING_CONSENT_STORAGE_KEY = "meta_marketing_consent_v1"
const META_MARKETING_CONSENT_COOKIE_NAME = "_meta_marketing_consent"

export type MetaMarketingConsent = "granted" | "denied" | "unknown"
export type MetaMarketingConsentPolicy = "allow_unknown" | "require_granted"

export const canUseMetaMarketing = ({
  consent,
  policy,
}: {
  consent: MetaMarketingConsent
  policy: MetaMarketingConsentPolicy
}): boolean =>
  consent !== "denied" &&
  (policy !== "require_granted" || consent === "granted")

export type MetaBrowserSignals = {
  fbp?: string
  fbc?: string
  externalId?: string
  eventSourceUrl?: string
  referrerUrl?: string
  consent: MetaMarketingConsent
}

const parseCookieValue = (cookieName: string): string | undefined => {
  if (typeof document === "undefined") {
    return undefined
  }

  let cookieHeader = ""
  try {
    cookieHeader = document.cookie || ""
  } catch {
    return undefined
  }
  const cookieEntries = cookieHeader ? cookieHeader.split(";") : []
  for (const entry of cookieEntries) {
    const [rawKey, ...rawValue] = entry.split("=")
    if (!rawKey) continue
    if (rawKey.trim() !== cookieName) continue
    const value = rawValue.join("=").trim()
    if (!value) return undefined
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  return undefined
}

const generateAnonymousExternalId = (): string | undefined =>
  tryCreateCryptographicBrowserId()?.toLowerCase()

const resolveAnonymousExternalId = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined
  }

  let localValue: string | null = null
  let sessionValue: string | null = null
  try {
    localValue = window.localStorage.getItem(META_EXTERNAL_ID_STORAGE_KEY)
  } catch {
    localValue = null
  }
  try {
    sessionValue = window.sessionStorage?.getItem(META_EXTERNAL_ID_STORAGE_KEY) || null
  } catch {
    sessionValue = null
  }
  const stored =
    localValue ||
    sessionValue ||
    parseCookieValue(META_EXTERNAL_ID_COOKIE_NAME)
  if (stored && stored.trim()) {
    return stored.trim().toLowerCase()
  }

  const generated = generateAnonymousExternalId()
  if (!generated) return undefined
  let persisted = false
  try {
    window.localStorage.setItem(META_EXTERNAL_ID_STORAGE_KEY, generated)
    persisted = true
  } catch {
    // ignore storage quota / privacy mode
  }
  try {
    window.sessionStorage?.setItem(META_EXTERNAL_ID_STORAGE_KEY, generated)
    persisted = true
  } catch {
    // Session storage is only a fallback for restrictive browsers.
  }
  try {
    const secureFlag = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${META_EXTERNAL_ID_COOKIE_NAME}=${encodeURIComponent(generated)}; Path=/; Max-Age=${60 * 60 * 24 * 365 * 2}; SameSite=Lax${secureFlag}`
    persisted = true
  } catch {
    // Cookie writes can be blocked independently from storage.
  }
  return persisted ? generated : undefined
}

const expireFirstPartyCookie = (cookieName: string): void => {
  if (typeof window === "undefined" || typeof document === "undefined") return
  const secureFlag = window.location.protocol === "https:" ? "; Secure" : ""
  const base = `${cookieName}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secureFlag}`
  const hostname = window.location.hostname || ""
  const labels = hostname.split(".").filter(Boolean)
  const domains = new Set<string>()
  if (labels.length >= 2) domains.add(`.${labels.join(".")}`)
  if (labels.length >= 3) domains.add(`.${labels.slice(1).join(".")}`)
  try {
    document.cookie = base
    domains.forEach((domain) => {
      document.cookie = `${base}; Domain=${domain}`
    })
  } catch {
    // Cookie access is best-effort and must never affect storefront behavior.
  }
}

export const clearMetaBrowserIdentifiers = (): void => {
  if (typeof window === "undefined") return
  try {
    window.localStorage.removeItem(META_EXTERNAL_ID_STORAGE_KEY)
  } catch {
    // Storage can be unavailable in privacy modes.
  }
  try {
    window.sessionStorage?.removeItem(META_EXTERNAL_ID_STORAGE_KEY)
  } catch {
    // Storage can be unavailable in privacy modes.
  }
  for (const cookieName of [META_EXTERNAL_ID_COOKIE_NAME, "_fbp", "_fbc"]) {
    expireFirstPartyCookie(cookieName)
  }
}

export const getMetaMarketingConsent = (): MetaMarketingConsent => {
  if (typeof window === "undefined") return "unknown"
  let localValue: string | null = null
  try {
    localValue = window.localStorage.getItem(META_MARKETING_CONSENT_STORAGE_KEY)
  } catch {
    localValue = null
  }
  const stored = localValue || parseCookieValue(META_MARKETING_CONSENT_COOKIE_NAME)
  return stored === "granted" || stored === "denied" ? stored : "unknown"
}

export const setMetaMarketingConsent = (
  consent: MetaMarketingConsent
): void => {
  if (typeof window === "undefined") return
  const normalized =
    consent === "granted" || consent === "denied" ? consent : "unknown"
  try {
    if (normalized === "unknown") {
      window.localStorage.removeItem(META_MARKETING_CONSENT_STORAGE_KEY)
    } else {
      window.localStorage.setItem(META_MARKETING_CONSENT_STORAGE_KEY, normalized)
    }
  } catch {
    // Storage can be unavailable in private browsing; the cookie remains a fallback.
  }
  try {
    const secureFlag = window.location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${META_MARKETING_CONSENT_COOKIE_NAME}=${normalized}; Path=/; Max-Age=${60 * 60 * 24 * 365}; SameSite=Lax${secureFlag}`
  } catch {
    // The in-memory consent event remains usable when cookies are blocked.
  }
  if (normalized === "denied") {
    clearMetaBrowserIdentifiers()
  }
  try {
    window.dispatchEvent(
      new CustomEvent("meta:marketing-consent", { detail: normalized })
    )
  } catch {
    // Event dispatch is optional in non-standard browser environments.
  }
}

export const getMetaBrowserSignals = ({
  allowIdentifiers = false,
}: {
  allowIdentifiers?: boolean
} = {}): MetaBrowserSignals => {
  if (typeof window === "undefined") {
    return { consent: "unknown" }
  }

  const consent = getMetaMarketingConsent()
  let eventSourceUrl: string | undefined
  let referrerUrl: string | undefined
  try {
    eventSourceUrl = window.location.href || undefined
  } catch {
    eventSourceUrl = undefined
  }
  try {
    referrerUrl = document.referrer || undefined
  } catch {
    referrerUrl = undefined
  }
  if (!allowIdentifiers || consent === "denied") {
    return {
      ...(eventSourceUrl ? { eventSourceUrl } : {}),
      ...(referrerUrl ? { referrerUrl } : {}),
      consent,
    }
  }

  // Reading is intentionally opaque. Cookie creation, refresh, and version
  // appendix handling belong exclusively to Meta's client Parameter Builder.
  let fbp: string | undefined
  let fbc: string | undefined
  try {
    ;({ fbp, fbc } = readMetaClientParams())
  } catch {
    fbp = undefined
    fbc = undefined
  }
  const externalId = resolveAnonymousExternalId()

  return {
    ...(fbp ? { fbp } : {}),
    ...(fbc ? { fbc } : {}),
    ...(externalId ? { externalId } : {}),
    ...(eventSourceUrl ? { eventSourceUrl } : {}),
    ...(referrerUrl ? { referrerUrl } : {}),
    consent,
  }
}

export const getAuthorizationHeaderFromCookies = (): string | undefined => {
  const token = parseCookieValue(AUTH_TOKEN_COOKIE_NAME)
  if (!token || !token.trim()) {
    return undefined
  }
  return `Bearer ${token.trim()}`
}
